// Phone health (2026-10-09): the app's own error log and a small heartbeat, so a crash or a full
// disk on a phone is seen in Ylläpito instead of never. Shared by the app (what it records and
// sends, app/src/health/) and the Worker (what it accepts and stores, api/src/routes/health.ts).
//
// Privacy: an error report carries no user text. Messages are scrubbed (quoted parts, words with
// non-ASCII letters — Finnish and High Valyrian —, numbers, ids, e-mail addresses and URLs' hosts and
// queries go), stack frames keep only the function name and the file's base name, and the route
// keeps only plain path words (`/sanakirja/:x`). Letters, typed answers and translations never
// appear in an error the app writes; the scrub is for errors thrown by the browser or a library.
// The Worker scrubs again: the client is not trusted to have done it.
import { cleanCoachDiagnostics, type CoachDiagnostics } from './coach/diagnostics';

export type ClientEventKind = 'error' | 'rejection' | 'render';
export const CLIENT_EVENT_KINDS: readonly ClientEventKind[] = ['error', 'rejection', 'render'];

/** One distinct error (by hash), with how often and when this device saw it. */
export interface ClientEvent {
  /** eventHash(kind, message, frames): the same error on the same code is one row. */
  hash: string;
  kind: ClientEventKind;
  message: string;
  /** The top stack frames, `fn@file.js:line:col`. */
  frames: string[];
  /** Occurrences since the last report (the server adds them up). */
  count: number;
  firstSeen: string;
  lastSeen: string;
  build: string;
  route: string;
  ua: string;
}

/** The device's state, sent with a sync now and then (HEALTH_LIMITS.heartbeatEveryMs). */
export interface Heartbeat {
  /** A random id this device made for itself (no fingerprinting). */
  device: string;
  build: string;
  /** navigator.storage.persisted(): true = the browser will not evict the app's data. null = unknown. */
  persisted: boolean | null;
  /** navigator.storage.estimate(), bytes. */
  usage: number | null;
  quota: number | null;
  /** Items waiting in the outboxes, and the age of the oldest (ms). */
  outbox: number;
  outboxOldestMs: number | null;
  /** iOS version from the user agent ("17.5.1"), null elsewhere. */
  ios: string | null;
  /** Installed to the home screen (display-mode standalone). */
  standalone: boolean | null;
  ua: string;
  at: string;
}

/** POST /api/health/report */
export interface HealthReport {
  events: ClientEvent[];
  heartbeat?: Heartbeat;
  /** Ääntämisvalmentaja: this device's latest coach diagnostics, when new ones wait (engine/src/coach/diagnostics.ts). */
  coach?: CoachDiagnostics;
}

/** GET /api/health/summary (admin): rows as stored, newest first. */
export interface HealthSummary {
  events: (ClientEvent & { userId: string })[];
  heartbeats: (Heartbeat & { userId: string })[];
  /** Ääntämisvalmentaja: each device's latest coach diagnostics (absent from an older Worker). */
  coach?: (CoachDiagnostics & { userId: string; device: string })[];
}

export const HEALTH_LIMITS = {
  /** Events per report. */
  eventsPerReport: 20,
  frames: 5,
  /** Frames the hash is made of. */
  hashFrames: 3,
  message: 300,
  frame: 160,
  route: 120,
  ua: 300,
  build: 40,
  /** Server: client_event rows kept (oldest last_seen pruned). */
  storedEvents: 500,
  /** Server: client_heartbeat rows kept (one per device; oldest pruned). */
  storedHeartbeats: 20,
  /** Device: distinct errors kept until sent. */
  localEvents: 50,
  /** Device: errors recorded per page load at most (a loop must not flood the log). */
  perPage: 200,
  /** Device: a heartbeat goes with a sync at most this often (an error report goes at once). */
  heartbeatEveryMs: 30 * 60_000,
  maxCount: 1_000_000_000,
} as const;

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * An error message without user text: URLs keep only their path's last part, quoted parts become
 * "…" (except browser-quoted property paths: `reading 'x'`, `evaluating 'a.b'`), words with
 * non-ASCII letters, e-mail addresses, uuids and long numbers are replaced.
 */
export function scrubMessage(raw: string): string {
  // placeholders in the Unicode private use area (never in a real message)
  const CUT = '';
  let s = String(raw ?? '');
  s = s.replace(/\b[\w.+-]+@[\w-]+(\.[\w-]+)+\b/g, '<email>');
  s = s.replace(/\b[a-z][a-z0-9+.-]*:\/\/[^\s'"`)]+/gi, (url) => {
    const path = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '').replace(/[?#].*$/, '');
    const last = path.split('/').filter(Boolean).pop() ?? '';
    return last ? `.../${last}` : '...';
  });
  // browser-quoted property paths are code, not text: kept
  s = s.replace(/\b(reading|setting|evaluating) (['"])([\w$.[\]()]{1,80})\2/g, '$1 $3');
  s = s.replace(/'[^']*'|"[^"]*"|`[^`]*`|“[^”]*”|‘[^’]*’|«[^»]*»/g, CUT);
  s = s.replace(/([^]*)/g, "'$1'");
  // any word with a non-ASCII letter (Finnish, High Valyrian, typed text) goes whole
  s = s.replace(/\S*[\u0080-￿]\S*/g, CUT);
  s = s.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<id>');
  s = s.replace(/\d{5,}/g, '<n>');
  s = s.replace(/(\s*)*/g, '"…"');
  s = s.replace(/\s+/g, ' ').trim();
  return clip(s, HEALTH_LIMITS.message) || '(no message)';
}

/** One stack line as `fn@file.js:line:col` (V8 "at fn (url:l:c)" or WebKit "fn@url:l:c"), or null. */
export function scrubFrame(line: string): string | null {
  const t = line.trim();
  let fn = '';
  let loc = '';
  const v8 = /^at (?:(.+?) \()?(.+?)\)?$/.exec(t);
  const wk = /^(.*?)@(.+)$/.exec(t);
  if (v8 && t.startsWith('at ')) {
    fn = v8[1] ?? '';
    loc = v8[2] ?? '';
  } else if (wk) {
    fn = wk[1] ?? '';
    loc = wk[2] ?? '';
  } else return null;
  const m = /([^/\\?#]+?)(?:[?#][^:]*)?:(\d+):(\d+)$/.exec(loc);
  if (!m) return null;
  const name = fn.replace(/[^\w$.<>]/g, '').slice(0, 60) || '?';
  return clip(`${name}@${m[1]}:${m[2]}:${m[3]}`, HEALTH_LIMITS.frame);
}

export function topFrames(stack: string | undefined, n: number = HEALTH_LIMITS.frames): string[] {
  if (!stack) return [];
  const out: string[] = [];
  for (const line of stack.split('\n')) {
    const f = scrubFrame(line);
    if (f) out.push(f);
    if (out.length >= n) break;
  }
  return out;
}

/** The route without ids or user text: plain lowercase path words stay, anything else is `:x`. */
export function scrubRoute(pathname: string): string {
  const path = String(pathname ?? '').replace(/[?#].*$/, '');
  const parts = path
    .split('/')
    .filter(Boolean)
    .map((p) => (/^[a-z][a-z0-9-]{0,30}$/.test(p) ? p : ':x'));
  return clip(`/${parts.join('/')}`, HEALTH_LIMITS.route);
}

/** FNV-1a, 64 bits as 16 hex digits: stable, fast, no crypto needed (it only groups). */
export function fnv1a64(s: string): string {
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < s.length; i++) {
    h ^= BigInt(s.charCodeAt(i));
    h = (h * prime) & 0xffffffffffffffffn;
  }
  return h.toString(16).padStart(16, '0');
}

export function eventHash(kind: ClientEventKind, message: string, frames: readonly string[]): string {
  // line:col of a frame changes with every build: the hash uses file base (hash part dropped) and function
  const stable = frames.slice(0, HEALTH_LIMITS.hashFrames).map((f) => f.replace(/-[\w-]{6,}(\.\w+):\d+:\d+$/, '$1').replace(/:\d+:\d+$/, ''));
  return fnv1a64([kind, message, ...stable].join('\n'));
}

/** "17.5.1" from an iPhone/iPad user agent ("CPU iPhone OS 17_5_1 like Mac OS X"), else null. */
export function iosVersion(ua: string): string | null {
  const m = /\b(?:iPhone|iPad|iPod)\b.*?\bOS (\d+)_(\d+)(?:_(\d+))?\b/.exec(ua ?? '');
  return m ? [m[1], m[2], m[3]].filter(Boolean).join('.') : null;
}

// ---------- the Worker's side: accept only clean, bounded values ----------

const isoOr = (v: unknown, fallback: string, now: number): string => {
  if (typeof v !== 'string') return fallback;
  const t = Date.parse(v);
  if (Number.isNaN(t)) return fallback;
  // a phone's clock may be off: never in the future, never before the app existed
  return new Date(Math.min(Math.max(t, Date.UTC(2026, 0, 1)), now)).toISOString();
};
const short = (v: unknown, n: number, re?: RegExp): string => {
  // control characters become spaces
  const s =
    typeof v === 'string'
      ? Array.from(v, (ch) => (ch.charCodeAt(0) < 32 ? ' ' : ch))
          .join('')
          .trim()
      : '';
  return re && !re.test(s) ? '' : clip(s, n);
};
const intOr = (v: unknown, lo: number, hi: number): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(Math.round(v), lo), hi) : null;
const boolOr = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

const BUILD = /^[\w.-]{0,40}$/;
export const DEVICE_ID = /^[A-Za-z0-9-]{8,40}$/;

/**
 * A report as the Worker stores it: events scrubbed again, hashed again, bounded; invalid items
 * dropped (an error report never fails as a whole). Returns null when the body is not a report.
 */
export function cleanReport(body: unknown, now: number = Date.now()): HealthReport | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as Record<string, unknown>;
  const at = new Date(now).toISOString();
  const events: ClientEvent[] = [];
  for (const raw of Array.isArray(b.events) ? b.events.slice(0, HEALTH_LIMITS.eventsPerReport) : []) {
    if (!raw || typeof raw !== 'object') continue;
    const e = raw as Record<string, unknown>;
    const kind = CLIENT_EVENT_KINDS.includes(e.kind as ClientEventKind) ? (e.kind as ClientEventKind) : null;
    if (!kind) continue;
    const message = scrubMessage(typeof e.message === 'string' ? e.message : '');
    const frames = (Array.isArray(e.frames) ? e.frames : [])
      .filter((f): f is string => typeof f === 'string')
      .map((f) => (/^[\w$.<>?]+@[^\s/\\]+:\d+:\d+$/.test(f) ? clip(f, HEALTH_LIMITS.frame) : scrubFrame(f)))
      .filter((f): f is string => !!f)
      .slice(0, HEALTH_LIMITS.frames);
    const lastSeen = isoOr(e.lastSeen, at, now);
    events.push({
      hash: eventHash(kind, message, frames),
      kind,
      message,
      frames,
      count: intOr(e.count, 1, HEALTH_LIMITS.maxCount) ?? 1,
      firstSeen: isoOr(e.firstSeen, lastSeen, now),
      lastSeen,
      build: short(e.build, HEALTH_LIMITS.build, BUILD),
      route: scrubRoute(typeof e.route === 'string' ? e.route : ''),
      ua: short(e.ua, HEALTH_LIMITS.ua),
    });
  }
  const report: HealthReport = { events };
  const h = b.heartbeat;
  if (h && typeof h === 'object' && !Array.isArray(h)) {
    const x = h as Record<string, unknown>;
    const device = typeof x.device === 'string' && DEVICE_ID.test(x.device) ? x.device : null;
    if (device) {
      const ua = short(x.ua, HEALTH_LIMITS.ua);
      report.heartbeat = {
        device,
        build: short(x.build, HEALTH_LIMITS.build, BUILD),
        persisted: boolOr(x.persisted),
        usage: intOr(x.usage, 0, Number.MAX_SAFE_INTEGER),
        quota: intOr(x.quota, 0, Number.MAX_SAFE_INTEGER),
        outbox: intOr(x.outbox, 0, 1_000_000) ?? 0,
        outboxOldestMs: intOr(x.outboxOldestMs, 0, 10 * 365 * 86_400_000),
        ios: iosVersion(ua),
        standalone: boolOr(x.standalone),
        ua,
        at,
      };
    }
  }
  // Ääntämisvalmentaja's device diagnostics (numbers and fixed words only)
  const coach = b.coach === undefined ? null : cleanCoachDiagnostics(b.coach, now);
  if (coach) report.coach = coach;
  return report;
}
